import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box, Typography, Alert, Button, Collapse, Select, MenuItem,
  FormControl, InputLabel, IconButton, Popover, CircularProgress,
  Chip, Tooltip as MuiTooltip, Divider,
} from "@mui/material";
import {
  Refresh, Download, ExpandMore, ExpandLess,
  Map as MapIcon, AutoAwesome, Tune, ArrowBack,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar, LabelList,
  ComposedChart, Line, LineChart,
} from "recharts";
import { format, subDays, parseISO } from "date-fns";
import * as XLSX from "xlsx";
import { reportsAPI } from "../services/api";
import ngaLga from "../data/nga_lga.json";

// ── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg: "#f0f4f7", white: "#ffffff", panel: "#f7f9fb",
  ink: "#2e4057", muted: "#7a8fa6", border: "#dde6ef", border2: "#ccd7e4",
  teal: "#0a7b74", tealLt: "#e4f3f2",
  navy: "#1a3a5c", navyLt: "#e6edf5",
  green: "#1b6e47", greenLt: "#e7f4ed",
  amber: "#a85200", amberLt: "#fdf0e3",
  red: "#b83535", redLt: "#fdeaea",
  blue: "#1a56a8", blueLt: "#e8f0fb",
  purple: "#6d28d9", purpleLt: "#f3effe",
};
const SERIF = "'Georgia', serif";
const MONO  = "'Courier New', monospace";
const SANS  = "'Segoe UI', system-ui, sans-serif";
const SHADOW = "0 1px 4px rgba(13,27,42,.08), 0 1px 2px rgba(13,27,42,.05)";
const SHADOW_LG = "0 6px 28px rgba(13,27,42,.14)";

const PRESETS = [
  { label: "Today", days: 0 },
  { label: "7D",    days: 7 },
  { label: "30D",   days: 30 },
  { label: "90D",   days: 90 },
];

// ── Tiny layout primitives ──────────────────────────────────────────────────
const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontFamily: MONO, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.teal, mb: 0.75 }}>
    {children}
  </Typography>
);

const SectionDivider = ({ num, label }: { num: string; label: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, mt: 3.5 }}>
    <Box sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1.5, color: C.muted, border: `1px solid ${C.border2}`, px: 1.25, py: 0.4, borderRadius: 20, background: C.white, whiteSpace: "nowrap" }}>
      {num}
    </Box>
    <Typography sx={{ fontFamily: SANS, fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: 1, background: C.border }} />
  </Box>
);

const Card = ({ title, sub, children, sx: extra = {}, action }: any) => (
  <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 2, p: "20px 20px 16px", boxShadow: SHADOW, ...extra }}>
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: title ? 0.5 : 0 }}>
      <Box>
        {title && <Typography sx={{ fontFamily: SERIF, fontSize: 15, fontWeight: 400, color: C.navy, letterSpacing: -.2 }}>{title}</Typography>}
        {sub && <Typography sx={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>{sub}</Typography>}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
    {children}
  </Box>
);

// ── Chart Settings Button (⚙) + Popover ─────────────────────────────────────
interface ChartField {
  axisLabel: string;
  chips: Array<{ label: string; color: string }>;
  options: string[];
}
interface ChartSettingsProps {
  csvData?: any[];
  csvFilename?: string;
  chartTypes?: string[];
  activeType?: string;
  onTypeChange?: (t: string) => void;
  fields?: ChartField[];
  chartRef?: React.RefObject<HTMLDivElement>;
  pngFilename?: string;
  storageKey?: string;  // localStorage key to persist preferred chart type
}
const ChartSettingsButton: React.FC<ChartSettingsProps> = ({
  csvData, csvFilename, chartTypes = [], activeType, onTypeChange, fields = [],
  chartRef, pngFilename, storageKey,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [savedDefault, setSavedDefault] = useState<string | null>(
    storageKey ? localStorage.getItem(storageKey) : null
  );

  const handleSetDefault = () => {
    if (!storageKey || !activeType) return;
    localStorage.setItem(storageKey, activeType);
    setSavedDefault(activeType);
    setAnchor(null);
  };

  const handleCsv = () => {
    if (!csvData?.length) return;
    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, csvFilename || "chart_data.xlsx");
    setAnchor(null);
  };

  const handlePng = async () => {
    const el = chartRef?.current;
    if (!el) return;
    setAnchor(null);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(el, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = pngFilename || (csvFilename?.replace(/\.\w+$/, "") || "chart") + ".png";
      a.click();
    } catch (e) {
      console.error("PNG export failed:", e);
    }
  };

  const menuRow = (icon: string, label: string, onClick?: () => void) => (
    <Box onClick={onClick} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, py: 0.85, cursor: "pointer", fontSize: 12, color: C.ink, transition: "background .15s", "&:hover": { background: C.panel }, borderRadius: 1 }}>
      <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>{icon}</span>
      {label}
    </Box>
  );

  const sectionLabel = (txt: string) => (
    <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, px: 1.75, pt: 1.25, pb: 0.4, borderTop: `1px solid ${C.border}`, mt: 0.5 }}>
      {txt}
    </Typography>
  );

  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}
        sx={{ width: 28, height: 28, border: `1px solid ${C.border}`, borderRadius: 1.5, color: C.muted, "&:hover": { background: C.tealLt, color: C.teal, transform: "scale(1.15) rotate(-8deg)" }, transition: "all .2s" }}>
        <AutoAwesome sx={{ fontSize: 14 }} />
      </IconButton>

      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 320, borderRadius: 2, border: `1px solid ${C.border2}`, boxShadow: SHADOW_LG, py: 0.5 } }}>

        {/* Download */}
        <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, px: 1.75, pt: 1.25, pb: 0.4 }}>Download</Typography>
        {menuRow("📄", "Download CSV", handleCsv)}
        {menuRow("🖼", "Download as PNG", handlePng)}

        {/* Chart type */}
        {chartTypes.length > 0 && (
          <>
            {sectionLabel("Chart Type")}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, px: 1.75, py: 0.75 }}>
              {chartTypes.map(t => (
                <Box key={t} onClick={() => { onTypeChange?.(t); setAnchor(null); }}
                  sx={{ px: 1.25, py: 0.4, borderRadius: 1, border: `1px solid ${activeType === t ? C.teal : C.border2}`, fontFamily: MONO, fontSize: 9, cursor: "pointer", color: activeType === t ? "#fff" : C.muted, background: activeType === t ? C.teal : "transparent", transition: "all .15s", "&:hover": activeType !== t ? { borderColor: C.teal, color: C.teal, background: C.tealLt } : {} }}>
                  {t}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Fields */}
        {fields.length > 0 && (
          <>
            {sectionLabel("Fields")}
            <Box sx={{ px: 1.75, pb: 1.25 }}>
              {fields.map((f, i) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.2, textTransform: "uppercase", color: C.muted, mb: 0.5 }}>{f.axisLabel}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
                    {f.chips.map((ch, j) => (
                      <Chip key={j} label={ch.label} size="small" onDelete={() => {}}
                        sx={{ height: 22, fontFamily: MONO, fontSize: 9, background: ch.color + "22", color: ch.color, border: `1px solid ${ch.color}`, "& .MuiChip-deleteIcon": { fontSize: 12, color: ch.color, "&:hover": { color: ch.color } } }} />
                    ))}
                  </Box>
                  <Select size="small" displayEmpty defaultValue="" fullWidth
                    sx={{ fontFamily: MONO, fontSize: 10, height: 28, "& .MuiSelect-select": { py: "4px" } }}>
                    <MenuItem value="" disabled sx={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>+ add field</MenuItem>
                    {f.options.map(o => <MenuItem key={o} value={o} sx={{ fontFamily: MONO, fontSize: 10 }}>{o}</MenuItem>)}
                  </Select>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.75, pb: 1.25, pt: 0.5, borderTop: `1px solid ${C.border}`, mt: 0.5 }}>
          <Button size="small" onClick={handleSetDefault}
            sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", borderRadius: 1, px: 1.25,
              ...(savedDefault === activeType
                ? { color: C.green, border: `1px solid ${C.green}`, background: C.greenLt }
                : { color: C.muted, border: `1px solid ${C.border2}` })
            }}>
            {savedDefault === activeType ? "✓ Default Set" : "Set Default"}
          </Button>
          <Button size="small" onClick={() => setAnchor(null)} sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", background: C.teal, color: "#fff", borderRadius: 1, px: 1.5, "&:hover": { background: "#086460" } }}>Apply</Button>
        </Box>
      </Popover>
    </>
  );
};

// ── KPI Card (grid layout) ───────────────────────────────────────────────────
interface KpiCellProps {
  label: string;
  value: string | number;
  unit?: string;
  note?: string;
  noteDir?: "up" | "down" | "neutral";
  accent: string;
  sparkData?: number[];
  today?: boolean;
}
const KpiCell: React.FC<KpiCellProps> = ({ label, value, unit, note, noteDir = "neutral", accent, sparkData, today }) => {
  const noteColor = noteDir === "up" ? C.green : noteDir === "down" ? C.red : C.muted;
  const spk = (sparkData || []).map(v => ({ v }));
  const hasSpark = spk.length > 1;
  const gradId = `spk-${label.replace(/\W/g, "")}`;
  return (
    <Box sx={{
      position: "relative", overflow: "hidden", minHeight: 110,
      background: C.white,
      border: today ? `1.5px solid ${accent}55` : `1px solid ${C.border}`,
      borderRadius: 2, boxShadow: today ? `0 0 0 2px ${accent}18, ${SHADOW}` : SHADOW,
      transition: "box-shadow .18s, transform .18s",
      "&:hover": { boxShadow: SHADOW_LG, transform: "translateY(-1px)" },
      "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent, borderRadius: "8px 8px 0 0" },
    }}>
      {/* Sparkline watermark — absolutely positioned behind text */}
      {hasSpark && (
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "70%", opacity: 0.09, pointerEvents: "none", zIndex: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spk} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={accent} stopOpacity={1} />
                  <stop offset="95%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area dataKey="v" stroke={accent} fill={`url(#${gradId})`} strokeWidth={2} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
      {/* Content — centered, above sparkline */}
      <Box sx={{ position: "relative", zIndex: 1, p: "14px 12px 12px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", height: "100%" }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: 1.5, textTransform: "uppercase", color: today ? accent : C.muted, mb: 0.75 }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: SERIF, fontSize: 28, lineHeight: 1, color: C.navy, fontWeight: 300, letterSpacing: -0.5, mb: 0.5 }}>
          {value}
          {unit && <span style={{ fontSize: 12, color: C.muted, fontFamily: SANS, letterSpacing: 0, marginLeft: 2 }}>{unit}</span>}
        </Typography>
        {note && (
          <Typography sx={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 500, color: noteColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
            {note}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ── Depth Histogram ─────────────────────────────────────────────────────────
const DEPTH_COLORS = [C.teal, C.blue, C.purple, C.amber, C.red];
const DEPTH_BUCKETS = ["1-2", "3-5", "6-8", "9-10", ">10"];

const DepthHistogram = ({ histogram, chartType = "H. Bar" }: { histogram: Array<{ bucket: string; count: number }>, chartType?: string }) => {
  const ORDERED = DEPTH_BUCKETS.map(b => histogram.find(h => h.bucket === b) ?? { bucket: b, count: 0 });
  const total = ORDERED.reduce((s, r) => s + r.count, 0) || 1;
  const maxCount = Math.max(...ORDERED.map(r => r.count), 1);

  const DepthTip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const pct = Math.round((payload[0].value / total) * 100);
    return (
      <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, boxShadow: SHADOW }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.navy }}>{payload[0]?.payload?.name ?? payload[0]?.name} msgs</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: payload[0]?.fill ?? DEPTH_COLORS[0] }}>{(payload[0]?.value || 0).toLocaleString()} users · {pct}%</Typography>
      </Box>
    );
  };

  // V. Bar — vertical bars
  if (chartType === "V. Bar") {
    const barData = ORDERED.map((row, i) => ({ name: row.bucket, count: row.count, fill: DEPTH_COLORS[i] }));
    return (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={barData} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
          <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
          <Tooltip content={<DepthTip />} cursor={{ fill: C.panel }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive animationDuration={600}>
            {barData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            <LabelList dataKey="count" position="top" style={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Donut or Pie
  if (chartType === "Donut" || chartType === "Pie") {
    const pieData = ORDERED.map((row) => ({ name: row.bucket, value: row.count }));
    const isDonut = chartType === "Donut";
    return (
      <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
        <Box sx={{ flexShrink: 0 }}>
          <PieChart width={200} height={200}>
            <Pie data={pieData} cx={100} cy={100} innerRadius={isDonut ? 52 : 0} outerRadius={88} dataKey="value" strokeWidth={3} stroke={C.white} isAnimationActive animationDuration={700}>
              {pieData.map((_, i) => <Cell key={i} fill={DEPTH_COLORS[i]} />)}
            </Pie>
            <Tooltip content={<DepthTip />} />
          </PieChart>
        </Box>
        <Box sx={{ flex: 1 }}>
          {ORDERED.map((row, i) => {
            const pct = Math.round((row.count / total) * 100);
            return (
              <Box key={row.bucket} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.9 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: DEPTH_COLORS[i], flexShrink: 0 }} />
                <Typography sx={{ flex: 1, fontFamily: MONO, fontSize: 11, color: C.ink }}>{row.bucket} msgs</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color: DEPTH_COLORS[i] }}>{row.count.toLocaleString()}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, width: 32, textAlign: "right" }}>{pct}%</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Default: H. Bar (horizontal histogram)
  return (
    <Box>
      {ORDERED.map((row, i) => {
        const pct = Math.round((row.count / total) * 100);
        const barW = Math.round((row.count / maxCount) * 100);
        const color = DEPTH_COLORS[i];
        return (
          <Box key={row.bucket} sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.1 }}>
            <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, width: 60, flexShrink: 0 }}>{row.bucket} msgs</Typography>
            <Box sx={{ flex: 1, height: 24, background: C.panel, borderRadius: 1, overflow: "hidden" }}>
              <Box sx={{
                height: "100%", width: `${barW}%`, background: color, borderRadius: 1,
                display: "flex", alignItems: "center", pl: 1,
                transition: "width 1.1s cubic-bezier(.4,0,.2,1)",
              }}>
                {barW > 10 && <Typography sx={{ fontFamily: MONO, fontSize: 9, color: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}>{row.count.toLocaleString()}</Typography>}
              </Box>
            </Box>
            {barW <= 10 && <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.ink, width: 46, textAlign: "right", flexShrink: 0, fontWeight: 600 }}>{row.count.toLocaleString()}</Typography>}
            <Typography sx={{ fontFamily: MONO, fontSize: 9, color: C.muted, width: 30, textAlign: "right", flexShrink: 0 }}>{pct}%</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ── Sunburst (nested PieChart) — supports Sunburst/Donut/Pie/Bar + drill-down ─
const SunburstChart = ({ diseases, chartType = "Sunburst" }: { diseases: any, chartType?: string }) => {
  const [drillDisease, setDrillDisease] = useState<string | null>(null);
  const dis = diseases || {};
  const mal = dis.malaria || {};
  const hiv = dis.hiv     || {};
  const tb  = dis.tb      || {};

  const innerData = [
    { name: "Malaria", value: mal.total || 0, data: mal },
    { name: "HIV",     value: hiv.total || 0, data: hiv },
    { name: "TB",      value: tb.total  || 0, data: tb  },
  ];
  const innerColors = ["#c04a10", C.purple, C.blue];
  const total = innerData.reduce((s, d) => s + d.value, 0) || 1;

  const SunTip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, boxShadow: SHADOW, pointerEvents: "none" }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.navy }}>{payload[0]?.name}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: payload[0]?.payload?.fill || C.teal }}>{(payload[0]?.value || 0).toLocaleString()}</Typography>
      </Box>
    );
  };

  // ── Drill-down view (clicked a disease) ──────────────────────────────────
  if (drillDisease) {
    const idx  = innerData.findIndex(d => d.name === drillDisease);
    const dObj = innerData[idx];
    const color = innerColors[idx];
    const sevData = [
      { name: "Mild",     value: dObj.data.severity?.mild     || 0, color: C.green },
      { name: "Moderate", value: dObj.data.severity?.moderate || 0, color: C.amber },
      { name: "High",     value: dObj.data.severity?.high     || 0, color: C.red   },
    ];
    const sevTotal = sevData.reduce((s, d) => s + d.value, 0) || 1;
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Button size="small" startIcon={<ArrowBack sx={{ fontSize: 13 }} />} onClick={() => setDrillDisease(null)}
            sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: C.muted, border: `1px solid ${C.border2}`, borderRadius: 20, px: 1.5 }}>
            All Diseases
          </Button>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
          <Typography sx={{ fontFamily: SERIF, fontSize: 15, color: C.navy }}>{drillDisease} — Severity Breakdown</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, ml: "auto" }}>{dObj.value.toLocaleString()} total checks</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          <Box sx={{ flexShrink: 0, position: "relative" }}>
            <PieChart width={200} height={200}>
              <Pie data={sevData} cx={100} cy={100} innerRadius={52} outerRadius={88} dataKey="value" strokeWidth={3} stroke={C.white} isAnimationActive animationDuration={600}>
                {sevData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip content={<SunTip />} />
            </PieChart>
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", flexDirection: "column" }}>
              <Typography sx={{ fontFamily: SERIF, fontSize: 22, lineHeight: 1, color }}>
                {dObj.value.toLocaleString()}
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 7, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>total</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            {sevData.map(d => {
              const pct = Math.round((d.value / sevTotal) * 100);
              return (
                <Box key={d.name} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: d.color }} />
                      <Typography sx={{ fontFamily: MONO, fontSize: 11, color: C.ink }}>{d.name}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color: d.color }}>{d.value.toLocaleString()} <span style={{ color: C.muted, fontSize: 10 }}>({pct}%)</span></Typography>
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 3, background: C.panel, overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${pct}%`, background: d.color, borderRadius: 3, transition: "width 1s cubic-bezier(.4,0,.2,1)" }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Stacked Bar by disease × severity ────────────────────────────────────
  if (chartType === "Bar") {
    const barData = [
      { name: "Malaria", mild: mal.severity?.mild || 0, moderate: mal.severity?.moderate || 0, high: mal.severity?.high || 0 },
      { name: "HIV",     mild: hiv.severity?.mild || 0, moderate: hiv.severity?.moderate || 0, high: hiv.severity?.high || 0 },
      { name: "TB",      mild: tb.severity?.mild  || 0, moderate: tb.severity?.moderate  || 0, high: tb.severity?.high  || 0 },
    ];
    return (
      <Box>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={barData} margin={{ top: 4, right: 8, bottom: 4, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 11, fill: C.muted }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ fontFamily: MONO, fontSize: 11, border: `1px solid ${C.border}`, borderRadius: 8 }} />
            <Bar dataKey="mild"     name="Mild"     fill={C.green} stackId="a" />
            <Bar dataKey="moderate" name="Moderate" fill={C.amber} stackId="a" />
            <Bar dataKey="high"     name="High"     fill={C.red}   radius={[2,2,0,0]} stackId="a" />
          </BarChart>
        </ResponsiveContainer>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {[["Mild", C.green], ["Moderate", C.amber], ["High", C.red]].map(([n, c]) => (
            <Box key={n} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, background: c }} />
              <Typography sx={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>{n}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // ── Donut or Pie ──────────────────────────────────────────────────────────
  if (chartType === "Donut" || chartType === "Pie") {
    const isDonut = chartType === "Donut";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ flexShrink: 0 }}>
          <PieChart width={220} height={220}>
            <Pie data={innerData} cx={110} cy={110} innerRadius={isDonut ? 52 : 0} outerRadius={95} dataKey="value"
              strokeWidth={3} stroke={C.white} isAnimationActive animationDuration={700}
              onClick={(d: any) => setDrillDisease(d.name)}>
              {innerData.map((_, i) => <Cell key={i} fill={innerColors[i]} style={{ cursor: "pointer" }} />)}
            </Pie>
            <Tooltip content={<SunTip />} />
          </PieChart>
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, mb: 1 }}>Click slice to drill down</Typography>
          {innerData.map((d, i) => {
            const pct = Math.round((d.value / total) * 100);
            return (
              <Box key={d.name} onClick={() => setDrillDisease(d.name)} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.9, cursor: "pointer", p: "4px 6px", borderRadius: 1, "&:hover": { background: C.panel } }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: innerColors[i], flexShrink: 0 }} />
                <Typography sx={{ flex: 1, fontSize: 12, color: C.ink }}>{d.name}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color: innerColors[i] }}>{d.value.toLocaleString()}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, width: 32, textAlign: "right" }}>{pct}%</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // ── Default: Sunburst (nested PieChart, inner=disease clickable → drill) ──
  const outerData = [
    { name: "Mal·Mild",  value: mal.severity?.mild     || 0 },
    { name: "Mal·Mod",   value: mal.severity?.moderate  || 0 },
    { name: "Mal·High",  value: mal.severity?.high      || 0 },
    { name: "HIV·Mild",  value: hiv.severity?.mild     || 0 },
    { name: "HIV·Mod",   value: hiv.severity?.moderate  || 0 },
    { name: "HIV·High",  value: hiv.severity?.high      || 0 },
    { name: "TB·Mild",   value: tb.severity?.mild      || 0 },
    { name: "TB·Mod",    value: tb.severity?.moderate   || 0 },
    { name: "TB·High",   value: tb.severity?.high       || 0 },
  ];
  const outerColors = [
    "#c04a10cc","#c04a1088","#c04a1055",
    C.purple+"cc",C.purple+"88",C.purple+"55",
    C.blue+"cc",C.blue+"88",C.blue+"55",
  ];
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ flexShrink: 0 }}>
          <PieChart width={240} height={240}>
            <Pie data={innerData} cx={120} cy={120} innerRadius={42} outerRadius={72} dataKey="value"
              strokeWidth={3} stroke={C.white} isAnimationActive
              onClick={(d: any) => setDrillDisease(d.name)}>
              {innerData.map((_, i) => <Cell key={i} fill={innerColors[i]} style={{ cursor: "pointer" }} />)}
            </Pie>
            <Pie data={outerData} cx={120} cy={120} innerRadius={78} outerRadius={108} dataKey="value" strokeWidth={2} stroke={C.white}>
              {outerData.map((_, i) => <Cell key={i} fill={outerColors[i]} />)}
            </Pie>
            <Tooltip content={<SunTip />} />
          </PieChart>
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, mb: 0.5 }}>Click inner ring to drill down</Typography>
          {[["Malaria", "#c04a10", mal.total || 0], ["HIV", C.purple, hiv.total || 0], ["TB", C.blue, tb.total || 0]].map(([n, color, v]: any) => (
            <Box key={n} onClick={() => setDrillDisease(n)} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.9, cursor: "pointer", p: "4px 6px", borderRadius: 1, "&:hover": { background: C.panel } }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
              <Typography sx={{ flex: 1, fontSize: 12, color: C.ink }}>{n}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color }}>{v.toLocaleString()}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, mb: 0.75 }}>Outer — Severity</Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {[["Mild", C.green], ["Moderate", C.amber], ["High", C.red]].map(([n, c]: any) => (
              <Box key={n} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: c }} />
                <Typography sx={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>{n}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── User Journey Funnel ─────────────────────────────────────────────────────
const FUNNEL_STAGES = [
  { key: "registered",       label: "Registered",       color: C.teal   },
  { key: "had_conversation", label: "Had Conversation",  color: C.blue   },
  { key: "symptom_checked",  label: "Symptom Check",    color: C.purple },
  { key: "searched_facility",label: "Facility Search",  color: C.amber  },
  { key: "knowledge_base",   label: "Knowledge Base",   color: C.green  },
];
const UserFunnel = ({ funnel, chartType = "Funnel" }: { funnel: any, chartType?: string }) => {
  const f = funnel || {};
  const base = f.registered || 1;
  const stageData = FUNNEL_STAGES.map(({ key, label, color }) => ({
    name: label, value: f[key] || 0, color,
    pct: Math.round(((f[key] || 0) / base) * 100),
  }));

  // H. Bar — horizontal recharts bar chart
  if (chartType === "H. Bar") {
    const FunnelTip = ({ active, payload }: any) => {
      if (!active || !payload?.length) return null;
      return (
        <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, boxShadow: SHADOW }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.navy }}>{payload[0]?.payload?.name}</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 11, color: payload[0]?.fill }}>{(payload[0]?.value || 0).toLocaleString()} users · {payload[0]?.payload?.pct}%</Typography>
        </Box>
      );
    };
    return (
      <ResponsiveContainer width="100%" height={Math.max(180, stageData.length * 42)}>
        <BarChart data={stageData} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 8 }} barSize={22}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
          <XAxis type="number" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="name" tick={{ fontFamily: MONO, fontSize: 10, fill: C.ink }} tickLine={false} axisLine={false} width={130} />
          <Tooltip content={<FunnelTip />} cursor={{ fill: C.tealLt }} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive animationDuration={600}>
            {stageData.map((d, i) => <Cell key={i} fill={d.color} />)}
            <LabelList dataKey="value" position="right" style={{ fontFamily: MONO, fontSize: 10, fill: C.muted }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Default: Funnel bars
  return (
    <Box>
      {stageData.map(({ name, value, color, pct }) => (
        <Box key={name} sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.9 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, width: 120, flexShrink: 0, whiteSpace: "nowrap" }}>{name}</Typography>
          <Box sx={{ flex: 1, height: 26, background: C.panel, borderRadius: 1, overflow: "hidden" }}>
            <Box sx={{
              height: "100%", width: `${pct}%`, background: color, borderRadius: 1,
              display: "flex", alignItems: "center", justifyContent: "space-between", px: 1,
              transition: "width 1s cubic-bezier(.4,0,.2,1)",
            }}>
              {pct > 18 && <Typography sx={{ fontFamily: MONO, fontSize: 10, color: "#fff", fontWeight: 600 }}>{value.toLocaleString()}</Typography>}
              {pct > 30 && <Typography sx={{ fontFamily: MONO, fontSize: 9, color: "rgba(255,255,255,.8)" }}>{pct}%</Typography>}
            </Box>
          </Box>
          {pct <= 18 && <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.ink, width: 46, textAlign: "right", flexShrink: 0 }}>{value.toLocaleString()}</Typography>}
          {pct <= 30 && <Typography sx={{ fontFamily: MONO, fontSize: 9, color: C.muted, width: 32, textAlign: "right", flexShrink: 0 }}>{pct}%</Typography>}
        </Box>
      ))}
    </Box>
  );
};

// ── Feedback Chart (switchable type) — Donut/Trend/Bar/Pie + entries ────────
type FbChartType = "Donut" | "Trend" | "Bar" | "Pie";
interface FeedbackChartProps {
  fb: any;
  trends?: any[];
  showAll: boolean;
  onToggleAll: () => void;
  onExport: () => void;
}
const FeedbackChart: React.FC<FeedbackChartProps> = ({ fb, trends = [], showAll, onToggleAll, onExport }) => {
  const [type, setType] = useState<FbChartType>("Donut");
  const cardRef = useRef<HTMLDivElement>(null);
  const f = fb || {};
  const sentimentData = [
    { name: "Positive", value: f.positive || 0, color: C.green },
    { name: "Neutral",  value: f.neutral  || 0, color: C.amber },
    { name: "Negative", value: f.negative || 0, color: C.red   },
  ];
  const sentimentTotal = sentimentData.reduce((s, d) => s + d.value, 0) || 1;
  const csvData = sentimentData.map(d => ({ sentiment: d.name, count: d.value }));

  const FbTip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const pct = Math.round((payload[0].value / sentimentTotal) * 100);
    return (
      <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, boxShadow: SHADOW }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.navy }}>{payload[0]?.name}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: payload[0]?.fill ?? payload[0]?.color ?? C.teal }}>{(payload[0]?.value || 0).toLocaleString()} · {pct}%</Typography>
      </Box>
    );
  };

  const TrendTip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, boxShadow: SHADOW }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.navy, mb: 0.5 }}>{label}</Typography>
        {payload.map((p: any) => (
          <Typography key={p.dataKey} sx={{ fontFamily: MONO, fontSize: 11, color: p.color }}>{p.name}: {(p.value || 0).toLocaleString()}</Typography>
        ))}
      </Box>
    );
  };

  const renderChart = () => {
    if (type === "Trend") {
      const trendFb = trends.length > 0 ? trends.map((d: any) => ({
        date: d.date,
        positive:  d.feedback_positive  || 0,
        neutral:   d.feedback_neutral   || 0,
        negative:  d.feedback_negative  || 0,
      })) : [];
      return (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={trendFb} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="fbPos" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.green} stopOpacity={0.2}/><stop offset="95%" stopColor={C.green} stopOpacity={0}/></linearGradient>
              <linearGradient id="fbNeg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.red}   stopOpacity={0.2}/><stop offset="95%" stopColor={C.red}   stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="date" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
            <Tooltip content={<TrendTip />} />
            <Area type="monotone" dataKey="positive" name="Positive" stroke={C.green} fill="url(#fbPos)" strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="neutral"  name="Neutral"  stroke={C.amber} fill="none"         strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            <Area type="monotone" dataKey="negative" name="Negative" stroke={C.red}   fill="url(#fbNeg)" strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
    if (type === "Bar") {
      return (
        <ResponsiveContainer width={220} height={160}>
          <BarChart data={sentimentData} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
            <Tooltip content={<FbTip />} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive animationDuration={600}>
              {sentimentData.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    // Donut or Pie
    const isDonut = type === "Donut";
    return (
      <Box sx={{ position: "relative", width: 160, height: 160, flexShrink: 0 }}>
        <PieChart width={160} height={160}>
          <Pie data={sentimentData} cx={80} cy={80} innerRadius={isDonut ? 48 : 0} outerRadius={72} dataKey="value" strokeWidth={3} stroke={C.white} isAnimationActive animationDuration={700}>
            {sentimentData.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <Tooltip content={<FbTip />} />
        </PieChart>
        {isDonut && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <Typography sx={{ fontFamily: SERIF, fontSize: 28, lineHeight: 1, color: C.teal }}>{f.positive_rate || 0}%</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 8, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>positive</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const entryMeta: Record<string, { color: string; bg: string; label: string }> = {
    yes:      { color: C.green, bg: C.greenLt, label: "Positive" },
    somewhat: { color: C.amber, bg: C.amberLt, label: "Neutral"  },
    no:       { color: C.red,   bg: C.redLt,   label: "Negative" },
  };

  return (
    <Box ref={cardRef}>
    <Card title="User Feedback" sub={`${(f.total || 0).toLocaleString()} submissions · use ⚙ to switch chart type`}
      action={
        <ChartSettingsButton
          csvData={csvData} csvFilename="feedback.xlsx"
          chartTypes={["Donut", "Trend", "Bar", "Pie"]} activeType={type} onTypeChange={t => setType(t as FbChartType)}
          chartRef={cardRef} pngFilename="feedback.png"
          fields={[
            { axisLabel: "📅 X — Date / Dimension", chips: [{ label: "📅 date (daily)", color: C.muted }], options: ["date (daily)", "date (weekly)", "satisfaction_type", "source", "state"] },
            { axisLabel: "📊 Y — Feedback Metric",  chips: [{ label: "positive_rate",  color: C.green }], options: ["positive_rate", "response_count", "percentage", "avg_rating", "negative_rate"] },
            { axisLabel: "🗂 Group By",              chips: [{ label: "satisfaction_type", color: C.muted }], options: ["satisfaction_type", "source", "state", "language"] },
          ]}
        />
      }>
      {/* Chart + legend row */}
      {type === "Trend" ? (
        renderChart()
      ) : (
        <Box sx={{ display: "flex", gap: 2.5, alignItems: "center", flexWrap: "wrap" }}>
          {renderChart()}
          <Box sx={{ flex: 1 }}>
            {sentimentData.map(d => {
              const pct = Math.round((d.value / sentimentTotal) * 100);
              return (
                <Box key={d.name} sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, borderBottom: `1px solid ${C.border}`, "&:last-child": { borderBottom: "none" } }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
                  <Typography sx={{ flex: 1, fontSize: 12, color: C.ink }}>{d.name}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color: d.color }}>{d.value.toLocaleString()}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, width: 32, textAlign: "right" }}>{pct}%</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Entries expand — inside the card, matching the mockup */}
      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
        <Button size="small" onClick={onToggleAll}
          sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: C.teal, border: `1px solid ${C.tealLt}`, borderRadius: 20, px: 2, py: 0.5, "&:hover": { background: C.tealLt } }}>
          {showAll ? "Hide Entries" : `Show All Entries (${(f.entries || []).length})`}
          <Box component="span" sx={{ ml: 0.75, display: "inline-block", transition: "transform .25s", transform: showAll ? "rotate(180deg)" : "rotate(0deg)" }}>▼</Box>
        </Button>
        <Button size="small" startIcon={<Download sx={{ fontSize: 13 }} />} onClick={onExport}
          sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: C.navy, border: `1px solid ${C.border2}`, borderRadius: 20, px: 2, py: 0.5, "&:hover": { borderColor: C.navy, background: C.navyLt } }}>
          Export Excel
        </Button>
      </Box>
      <Collapse in={showAll}>
        {(f.entries || []).length === 0 ? (
          <Typography sx={{ fontSize: 12, color: C.muted, py: 1 }}>No feedback in this period.</Typography>
        ) : (
          <Box sx={{ mt: 1.5, border: `1px solid ${C.border}`, borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "90px 160px 1fr 90px", gap: 1.5, p: "8px 16px", borderBottom: `1px solid ${C.border2}` }}>
              {["Sentiment", "Source", "Comment", "Date"].map(h => (
                <Typography key={h} sx={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted }}>{h}</Typography>
              ))}
            </Box>
            {(f.entries || []).map((entry: any, i: number) => {
              const m = entryMeta[entry.satisfaction] || { color: C.muted, bg: C.panel, label: entry.satisfaction || "—" };
              return (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "90px 160px 1fr 90px", gap: 1.5, p: "10px 16px", borderBottom: `1px solid ${C.border}`, alignItems: "start", "&:last-child": { borderBottom: "none" } }}>
                  <Box sx={{ fontFamily: MONO, fontSize: 9, px: 1, py: 0.3, borderRadius: 20, background: m.bg, color: m.color, fontWeight: 500, textAlign: "center" }}>{m.label}</Box>
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: C.ink }}>{entry.source || "—"}</Typography>
                  <Typography sx={{ fontSize: 12, color: entry.comment ? C.ink : C.muted, fontStyle: entry.comment ? "normal" : "italic" }}>{entry.comment || "No comment"}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{entry.date}</Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Collapse>
    </Card>
    </Box>
  );
};

// ── AI Insights Panel ───────────────────────────────────────────────────────
const AIInsights = ({ startDate, endDate, country, darkMode = false }: { startDate: Date; endDate: Date; country: string; darkMode?: boolean }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState<any>(null);
  const [error, setError]     = useState<string | null>(null);
  const textColor = darkMode ? "rgba(255,255,255,.85)" : C.ink;
  const mutedColor = darkMode ? "rgba(255,255,255,.4)" : C.muted;

  const fetchInsights = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await (reportsAPI as any).getInsights({
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date:   format(endDate,   "yyyy-MM-dd"),
        ...(country ? { country } : {}),
      });
      setData(result);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e.message || "Failed to generate AI insights";
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, country]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const getAnomalyStyle = (pct: number) => {
    const abs = Math.abs(pct);
    if (abs >= 30) return { bg: darkMode ? "#b8353522" : C.redLt,   border: C.red,   label: `🔴 ${pct > 0 ? "+" : ""}${pct}%` };
    if (abs >= 15) return { bg: darkMode ? "#a8520022" : C.amberLt, border: C.amber, label: `⚠ ${pct > 0 ? "+" : ""}${pct}%` };
    return { bg: darkMode ? "#0a7b7422" : C.tealLt, border: C.teal, label: `${pct > 0 ? "+" : ""}${pct}%` };
  };

  return (
    <Box sx={{ p: "16px 24px 20px" }}>
      {/* Refresh + timestamp row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        {data && <Typography sx={{ fontFamily: MONO, fontSize: 9, color: mutedColor }}>Generated: {data.generated_at ? format(new Date(data.generated_at), "d MMM yyyy HH:mm") : "—"}</Typography>}
        <Button size="small" onClick={fetchInsights}
          sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: C.teal, border: `1px solid ${C.teal}44`, borderRadius: 10, px: 1.5, minWidth: 0, ml: "auto" }}>
          {loading ? <CircularProgress size={10} sx={{ color: C.teal }} /> : "↻ Refresh"}
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>}
      {loading && !data && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
          <CircularProgress size={16} sx={{ color: C.teal }} />
          <Typography sx={{ fontFamily: MONO, fontSize: 11, color: mutedColor }}>Generating insights with Groq AI…</Typography>
        </Box>
      )}
      {/* Anomalies */}
      {(data?.anomalies || []).length > 0 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          {data.anomalies.map((a: any, i: number) => {
            const s = getAnomalyStyle(a.change_pct || 0);
            return (
              <Box key={i} sx={{ background: s.bg, border: `1px solid ${s.border}44`, px: 1.25, py: 0.5, borderRadius: 20, display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700, color: s.border }}>{s.label}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 8, color: s.border }}>{a.metric}</Typography>
              </Box>
            );
          })}
        </Box>
      )}
      {/* Insights */}
      {(data?.insights || []).map((ins: string, i: number) => (
        <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 1.25, alignItems: "flex-start" }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: [C.teal, "#4ade80", C.purple, C.amber, "#60a5fa"][i % 5], mt: "6px", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 13, color: textColor, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: ins.replace(/\*\*(.*?)\*\*/g, `<strong>$1</strong>`) }} />
        </Box>
      ))}
    </Box>
  );
};

// ── Donut with legend ───────────────────────────────────────────────────────
const DonutCard = ({ title, sub, data, colors, label, sublabel, csvData, csvFilename, chartTypes, fields }: any) => {
  const [type, setType] = useState("Donut");
  const cardRef = useRef<HTMLDivElement>(null);
  const total = data.reduce((s: number, d: any) => s + d.value, 0);

  const DnTip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const pct = total > 0 ? Math.round((payload[0].value / total) * 100) : 0;
    return (
      <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, boxShadow: SHADOW }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.navy }}>{payload[0]?.name}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: payload[0]?.fill }}>{(payload[0]?.value || 0).toLocaleString()} · {pct}%</Typography>
      </Box>
    );
  };

  return (
    <Box ref={cardRef} sx={{ height: "100%" }}>
    <Card title={title} sub={sub} sx={{ height: "100%" }}
      action={<ChartSettingsButton csvData={csvData} csvFilename={csvFilename} chartTypes={chartTypes || ["Donut", "Pie", "Bar"]} activeType={type} onTypeChange={setType} chartRef={cardRef} pngFilename={(csvFilename || "chart").replace(/\.\w+$/, "") + ".png"} fields={fields || []} />}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
        <Box sx={{ flexShrink: 0 }}>
          {(type === "Donut" || type === "Pie") ? (
            <Box sx={{ position: "relative", width: 130, height: 130 }}>
              <PieChart width={130} height={130}>
                <Pie data={data.length ? data : [{ name: "empty", value: 1 }]} cx={65} cy={65} innerRadius={type === "Donut" ? 40 : 0} outerRadius={60} dataKey="value" strokeWidth={3} stroke={C.white} isAnimationActive animationDuration={700}>
                  {data.length ? data.map((_: any, i: number) => <Cell key={i} fill={colors[i % colors.length]} />) : <Cell fill={C.border} />}
                </Pie>
                {data.length > 0 && <Tooltip content={<DnTip />} />}
              </PieChart>
              {type === "Donut" && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  <Typography sx={{ fontFamily: SERIF, fontSize: 24, lineHeight: 1, color: C.navy }}>{total > 0 ? label || total.toLocaleString() : "—"}</Typography>
                  {sublabel && <Typography sx={{ fontFamily: MONO, fontSize: 8, color: C.muted, textTransform: "uppercase", letterSpacing: 1, mt: 0.3 }}>{sublabel}</Typography>}
                </Box>
              )}
            </Box>
          ) : (
            <ResponsiveContainer width={140} height={130}>
              <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
                <Tooltip content={<DnTip />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive>
                  {data.map((_: any, i: number) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {data.map((d: any, i: number) => {
            const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
            return (
              <Box key={d.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: colors[i % colors.length], flexShrink: 0 }} />
                <Typography sx={{ flex: 1, fontSize: 12, color: C.ink }}>{d.name}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color: colors[i % colors.length] }}>{(d.value || 0).toLocaleString()}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, width: 32, textAlign: "right" }}>{pct}%</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Card>
    </Box>
  );
};

// ── Nigeria LGA choropleth (kept from original) ─────────────────────────────
const NGA_STATES: { n: string; p: [number, number][][] }[] = (ngaLga as any[]).map((f: any) => ({ n: f.name, p: f.p }));
const NIG = { minLat: 4.27, maxLat: 13.90, minLon: 2.67, maxLon: 14.68 };
const SVG_W = 560, SVG_H = 400, SVG_PAD = 28;
const geoProject = (lon: number, lat: number): [number, number] => {
  const x = SVG_PAD + ((lon - NIG.minLon) / (NIG.maxLon - NIG.minLon)) * (SVG_W - 2 * SVG_PAD);
  const y = SVG_PAD + ((NIG.maxLat - lat) / (NIG.maxLat - NIG.minLat)) * (SVG_H - 2 * SVG_PAD);
  return [x, y];
};
const pointInPoly = (lon: number, lat: number, ring: [number, number][]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};
const heatColor = (t: number) => `rgb(${Math.round(240 - t * 220)},${Math.round(243 - t * 120)},${Math.round(242 - t * 180)})`;
const FacilityMapEmbed = ({ points }: { points: any[] }) => {
  const [tip, setTip] = useState<{ x: number; y: number; name: string; count: number } | null>(null);
  const stateCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    points.forEach(p => {
      const lon = +p.lon, lat = +p.lat, inc = p.count || 1;
      for (const st of NGA_STATES) {
        if (st.p.some(ring => pointInPoly(lon, lat, ring))) { counts.set(st.n, (counts.get(st.n) || 0) + inc); break; }
      }
    });
    return counts;
  }, [points]);
  const maxCount = Math.max(1, ...Array.from(stateCounts.values()));
  return (
    <Box sx={{ position: "relative" }}>
      <svg width="100%" viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ display: "block", borderRadius: 8 }}>
        <rect x={0} y={0} width={SVG_W} height={SVG_H} fill="#dce8f5" rx={8} />
        {NGA_STATES.map(st => {
          const count = stateCounts.get(st.n) || 0;
          const fill = count > 0 ? heatColor(count / maxCount) : "#f0f4f7";
          return st.p.map((ring, ri) => {
            const pts = ring.map(([lon, lat]) => geoProject(lon, lat).join(",")).join(" ");
            return (
              <polygon key={`${st.n}-${ri}`} points={pts} fill={fill} stroke="#9dbfb7" strokeWidth={0.6} strokeLinejoin="round"
                style={{ cursor: count > 0 ? "pointer" : "default" }}
                onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, name: st.n, count })}
                onMouseMove={e => setTip(t2 => t2 ? { ...t2, x: e.clientX, y: e.clientY } : null)}
                onMouseLeave={() => setTip(null)} />
            );
          });
        })}
        {NGA_STATES.map(st => {
          const count = stateCounts.get(st.n) || 0;
          if (!count) return null;
          const ring = st.p[0];
          const cx = ring.reduce((s, c) => s + c[0], 0) / ring.length;
          const cy = ring.reduce((s, c) => s + c[1], 0) / ring.length;
          const [sx, sy] = geoProject(cx, cy);
          return <text key={`lbl-${st.n}`} x={sx} y={sy} textAnchor="middle" dominantBaseline="middle" fontSize={7.5} fill="#1a3a5c" fontFamily="monospace" fontWeight="600" style={{ pointerEvents: "none", userSelect: "none" }}>{st.n.replace(" Federal Capital Territory", " FCT").replace("Abuja ", "")}</text>;
        })}
        <defs><linearGradient id="heatLegend" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stopColor={heatColor(0)} /><stop offset="100%" stopColor={heatColor(1)} /></linearGradient></defs>
        <rect x={SVG_PAD} y={SVG_H - 22} width={80} height={8} fill="url(#heatLegend)" rx={2} stroke="#9dbfb7" strokeWidth={0.5} />
        <text x={SVG_PAD} y={SVG_H - 7} fontSize={8} fill="#7a8fa6" fontFamily="monospace">Low</text>
        <text x={SVG_PAD + 80} y={SVG_H - 7} fontSize={8} fill="#7a8fa6" fontFamily="monospace" textAnchor="end">High</text>
        <text x={SVG_W - SVG_PAD - 2} y={SVG_H - 7} textAnchor="end" fontSize={8} fill="#7a8fa6" fontFamily="monospace">Nigeria · facility searches by LGA</text>
      </svg>
      {tip && (
        <Box sx={{ position: "fixed", left: tip.x + 14, top: tip.y - 12, zIndex: 9999, background: C.navy, color: "#fff", px: 1.5, py: 1, borderRadius: 1.5, fontSize: 11, fontFamily: MONO, pointerEvents: "none", boxShadow: "0 3px 12px rgba(0,0,0,.28)", lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600 }}>{tip.name}</div>
          <div>Searches: {tip.count.toLocaleString()}</div>
        </Box>
      )}
    </Box>
  );
};

// ── HealthTopicsDrillDown (kept from original) ──────────────────────────────
const TOPIC_CATEGORIES: Record<string, string[]> = {
  "HIV / AIDS":      ["hiv","aids","antiretroviral","art","viral load","cd4","prep","pmtct"],
  "Tuberculosis":    ["tb","tuberculosis","rifampicin","isoniazid","dots","mdr","chest"],
  "Malaria":         ["malaria","fever","mosquito","artemisinin","rdt","nets","plasmodium"],
  "Prevention":      ["prevention","condom","vaccine","screening","testing","prophylaxis"],
  "Treatment":       ["treatment","medication","drug","dosage","therapy","adherence"],
  "Facility & Care": ["hospital","clinic","facility","doctor","nurse","health centre","ward"],
  "Symptoms":        ["symptom","pain","cough","fatigue","weight","rash","diarrhea"],
  "Other Topics":    [],
};
const CAT_COLORS = [C.teal, C.purple, "#c04a10", C.green, C.blue, C.navy, C.amber, C.muted];
const HealthTopicsDrillDown = ({ interests }: { interests: any[] }) => {
  const [drillCategory, setDrillCategory] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  if (!interests.length) return (
    <Card title="Top Health Topics" sub="AI-extracted from conversations · most frequent first">
      <Box sx={{ py: 4, textAlign: "center" }}><Typography sx={{ fontFamily: MONO, fontSize: 11, color: C.muted }}>No interest data yet</Typography></Box>
    </Card>
  );
  const topicCategory = (topic: string) => {
    const t = topic.toLowerCase();
    for (const [cat, kws] of Object.entries(TOPIC_CATEGORIES)) {
      if (cat === "Other Topics") continue;
      if (kws.some(kw => t.includes(kw))) return cat;
    }
    return "Other Topics";
  };
  const grouped: Record<string, { topics: any[]; total: number }> = {};
  for (const item of interests) {
    const cat = topicCategory(item.topic);
    if (!grouped[cat]) grouped[cat] = { topics: [], total: 0 };
    grouped[cat].topics.push(item);
    grouped[cat].total += item.count;
  }
  const categories = Object.entries(grouped).filter(([, v]) => v.total > 0).sort((a, b) => b[1].total - a[1].total);
  const BarTip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, boxShadow: SHADOW }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.navy, textTransform: "capitalize" }}>{payload[0]?.payload?.name}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: C.teal }}>{(payload[0]?.value || 0).toLocaleString()} mentions</Typography>
      </Box>
    );
  };
  if (drillCategory) {
    const catIdx = categories.findIndex(([k]) => k === drillCategory);
    const catColor = CAT_COLORS[catIdx % CAT_COLORS.length];
    const drillData = (grouped[drillCategory]?.topics || []).sort((a: any, b: any) => b.count - a.count).map((t: any) => ({ name: t.topic.replace(/_/g, " "), count: t.count }));
    return (
      <Card title={`Health Topics · ${drillCategory}`} sub={`${drillData.length} topics · ${(grouped[drillCategory]?.total || 0).toLocaleString()} mentions`}
        action={<Button size="small" onClick={() => setDrillCategory(null)} sx={{ fontFamily: MONO, fontSize: 9, color: C.muted, border: `1px solid ${C.border2}`, borderRadius: 20, px: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>← All</Button>}>
        <ResponsiveContainer width="100%" height={Math.max(200, drillData.length * 38)}>
          <BarChart data={drillData} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 8 }} barSize={18}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
            <XAxis type="number" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fontFamily: MONO, fontSize: 10, fill: C.ink }} tickLine={false} axisLine={false} width={160} />
            <Tooltip content={<BarTip />} cursor={{ fill: C.tealLt }} />
            <Bar dataKey="count" fill={catColor} radius={[0, 4, 4, 0]} isAnimationActive animationDuration={500}>
              <LabelList dataKey="count" position="right" style={{ fontFamily: MONO, fontSize: 10, fill: C.muted }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    );
  }
  const topData = categories.map(([name, v], i) => ({ name, count: v.total, color: CAT_COLORS[i % CAT_COLORS.length] }));
  const csvData = topData.map(d => ({ category: d.name, mentions: d.count }));
  return (
    <Box ref={cardRef}>
    <Card title="Top Health Topics" sub={`${interests.length} topics · ${categories.length} categories · click bar to drill`}
      action={<ChartSettingsButton csvData={csvData} csvFilename="health_topics.xlsx" chartTypes={["H. Bar", "V. Bar", "Donut"]}
        chartRef={cardRef} pngFilename="health_topics.png"
        fields={[
          { axisLabel: "🏷 Y — Topic Label", chips: [{ label: "topic_category", color: C.teal }], options: ["topic_category","topic_name","disease_tag"] },
          { axisLabel: "📊 X — Value",        chips: [{ label: "mention_count",  color: C.navy }], options: ["mention_count","percentage","unique_users"] },
        ]} />}>
      <ResponsiveContainer width="100%" height={Math.max(200, topData.length * 46)}>
        <BarChart data={topData} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 8 }} barSize={24}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
          <XAxis type="number" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="name" tick={{ fontFamily: MONO, fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} width={155} />
          <Tooltip content={<BarTip />} cursor={{ fill: C.tealLt }} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} isAnimationActive animationDuration={600} onClick={(d: any) => { if (d?.name) setDrillCategory(d.name); }} style={{ cursor: "pointer" }}>
            {topData.map((d, i) => <Cell key={i} fill={d.color} />)}
            <LabelList dataKey="count" position="right" style={{ fontFamily: MONO, fontSize: 10, fill: C.muted }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
    </Box>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [data,         setData]         = useState<any>(null);
  const [mapPoints,    setMapPoints]    = useState<any[]>([]);
  const [startDate,    setStartDate]    = useState<Date>(subDays(new Date(), 30));
  const [endDate,      setEndDate]      = useState<Date>(new Date());
  const [activePreset, setActivePreset] = useState(30);
  const [showAllFb,       setShowAllFb]       = useState(false);
  const [filterOpen,      setFilterOpen]      = useState(false);
  const [country,         setCountry]         = useState<string>("NG");
  const [state,           setState]           = useState<string>("");
  // Chart type state — each reads its saved localStorage default on first render
  const ls = (key: string, fallback: string) => localStorage.getItem(key) || fallback;
  const [dualAxisType,    setDualAxisType]    = useState(() => ls("chart:trend",      "Line"));
  const [userGrowthType,  setUserGrowthType]  = useState(() => ls("chart:userGrowth", "Bar"));
  const [convVolumeType,  setConvVolumeType]  = useState(() => ls("chart:convVolume", "Bar"));
  const [depthType,       setDepthType]       = useState(() => ls("chart:depth",      "H. Bar"));
  const [diseaseType,     setDiseaseType]     = useState(() => ls("chart:disease",    "Bar"));
  const [funnelType,      setFunnelType]      = useState(() => ls("chart:funnel",     "Funnel"));

  // Chart container refs (for PNG export)
  const trendRef       = useRef<HTMLDivElement>(null);
  const userGrowthRef  = useRef<HTMLDivElement>(null);
  const convVolumeRef  = useRef<HTMLDivElement>(null);
  const depthRef       = useRef<HTMLDivElement>(null);
  const diseaseRef     = useRef<HTMLDivElement>(null);
  const funnelRef      = useRef<HTMLDivElement>(null);
  const topStatesRef   = useRef<HTMLDivElement>(null);
  const facilityMapRef = useRef<HTMLDivElement>(null);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    const params: any = {
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date:   format(endDate,   "yyyy-MM-dd"),
    };
    if (country) params.country = country;
    try {
      const [result, mapResult] = await Promise.all([
        reportsAPI.getOverview(params),
        reportsAPI.getFacilityMap(params).catch(() => ({ points: [] })),
      ]);
      setData(result);
      setMapPoints(mapResult.points || []);
    } catch (err: any) {
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, country]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyPreset = (days: number) => {
    setActivePreset(days);
    const today = new Date();
    setStartDate(days === 0 ? today : subDays(today, days));
    setEndDate(today);
  };

  // Shorthand accessors
  const reg    = data?.registrations    || {};
  const conv   = data?.conversations    || {};
  const msgs   = data?.messages         || {};
  const cmpl   = data?.complaints       || {};
  const checks = data?.symptom_checks   || {};
  const fb     = data?.feedback         || {};
  const tools  = data?.tools            || [];
  const sev    = checks.severity        || {};
  const dis    = checks.diseases        || { tb: {}, hiv: {}, malaria: {} };
  const depth  = data?.conversation_depth || {};
  const spk    = data?.sparklines       || {};

  // Compute cumulative trend data
  const trends = useMemo(() => {
    const raw = (data?.trends?.daily || []);
    return raw.map((d: any) => ({
      ...d,
      date: d.date ? format(parseISO(d.date), "MMM d") : d.date,
    }));
  }, [data]);

  const avgMsgPerUser = conv.distinct_contacts
    ? Math.round((msgs.incoming || 0) / conv.distinct_contacts)
    : 0;

  const presetBtn = (p: typeof PRESETS[0]) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, textTransform: "uppercase" as const,
    px: 1.5, py: 0.5, minWidth: 0, borderRadius: 20,
    ...(activePreset === p.days
      ? { background: C.teal, borderColor: C.teal, color: "#fff", "&:hover": { background: "#086460" } }
      : { borderColor: C.border2, color: C.muted, background: "transparent", "&:hover": { borderColor: C.teal, color: C.teal, background: C.tealLt } }),
  });

  const trendCsvData = trends.map((d: any) => ({
    date: d.date, conversations: d.conversations, messages: d.messages,
    new_registrations: d.new_registrations || 0, cumulative_conversations: d.cumulative_conversations || 0,
  }));

  // Build per-KPI sparklines from trend data for grid watermarks
  const kpiSparklines = useMemo(() => ({
    registrations:  trends.map((d: any) => d.new_registrations  || 0),
    messages:       trends.map((d: any) => d.messages           || 0),
    conversations:  trends.map((d: any) => d.conversations      || 0),
    fbPositive:     trends.map((d: any) => d.feedback_positive  || 0),
  }), [trends]);

  // Today's slice from trend data (for KPI notes)
  const todayTrend = useMemo(() => {
    const raw = data?.trends?.daily || [];
    if (!raw.length) return null;
    const todayStr = format(new Date(), "yyyy-MM-dd");
    return raw.find((d: any) => d.date === todayStr) || raw[raw.length - 1];
  }, [data]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ background: C.bg, minHeight: "100vh", fontFamily: SANS }}>
        <Box sx={{ maxWidth: 1360, mx: "auto", px: { xs: 2, md: 4 }, pt: "32px", pb: "64px" }}>

          {/* ── Header ──────────────────────────────────────────────────── */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, pb: 2.5, mb: 2, borderBottom: `1px solid ${C.border2}` }}>
            {/* Left — branding + date */}
            <Box>
              <Eyebrow>Sophia AI Health Platform</Eyebrow>
              <Typography sx={{ fontFamily: SERIF, fontSize: { xs: 22, md: 30 }, lineHeight: 1.05, color: C.navy, fontWeight: 300, letterSpacing: -.5 }}>
                National Health <em style={{ fontStyle: "italic", color: C.teal }}>Intelligence</em> Dashboard
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.75, flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted, letterSpacing: .5 }}>
                  {format(startDate, "MMM d")} – {format(endDate, "MMM d, yyyy")} &nbsp;·&nbsp; {country === "NG" ? "🇳🇬 Nigeria (+234)" : "🌍 All Countries"}
                </Typography>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.6,
                  fontFamily: MONO, fontSize: 9, color: C.green, letterSpacing: 1, textTransform: "uppercase",
                  background: C.greenLt, px: 1.25, py: 0.4, borderRadius: 20,
                  "&::before": { content: '""', width: 5, height: 5, borderRadius: "50%", background: C.green, flexShrink: 0, animation: "pulse 1.5s ease-in-out infinite" },
                  "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: .45 } },
                }}>Live</Box>
              </Box>
            </Box>

            {/* Right — AI Insights + Filter + Refresh */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, mt: 0.5 }}>
              {/* AI Narrative button */}
              <MuiTooltip title="Generate AI Narrative Insights" arrow placement="bottom">
                <Box onClick={() => setAiOpen(v => !v)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75,
                    background: aiOpen
                      ? `linear-gradient(135deg, ${C.navy} 0%, ${C.teal} 100%)`
                      : `linear-gradient(135deg, ${C.teal} 0%, ${C.navy} 100%)`,
                    color: "#fff", px: 1.75, py: 0.9, borderRadius: 2, cursor: "pointer",
                    fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase",
                    boxShadow: `0 2px 8px ${C.teal}44`,
                    transition: "all .2s", "&:hover": { transform: "translateY(-1px)", boxShadow: `0 4px 16px ${C.teal}55` },
                  }}>
                  <AutoAwesome sx={{ fontSize: 14 }} />
                  {aiOpen ? "Close Insights" : "AI Insights"}
                </Box>
              </MuiTooltip>

              {/* Filter button */}
              <MuiTooltip title="Filters — date range, country, state" arrow placement="bottom">
                <IconButton onClick={e => setFilterAnchor(e.currentTarget)}
                  sx={{
                    width: 38, height: 38, borderRadius: 2,
                    border: `1.5px solid ${filterAnchor ? C.teal : C.border2}`,
                    background: filterAnchor ? C.tealLt : C.white,
                    color: filterAnchor ? C.teal : C.muted,
                    "&:hover": { borderColor: C.teal, color: C.teal, background: C.tealLt },
                    transition: "all .2s",
                  }}>
                  <Tune sx={{ fontSize: 18 }} />
                </IconButton>
              </MuiTooltip>

              {/* Refresh */}
              <MuiTooltip title="Refresh data" arrow placement="bottom">
                <IconButton onClick={fetchData} disabled={loading}
                  sx={{ width: 38, height: 38, borderRadius: 2, border: `1.5px solid ${C.border2}`, color: C.muted, "&:hover": { borderColor: C.teal, color: C.teal }, transition: "all .2s" }}>
                  <Refresh sx={{ fontSize: 18, ...(loading ? { animation: "spin 1s linear infinite", "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } } : {}) }} />
                </IconButton>
              </MuiTooltip>
            </Box>
          </Box>

          {/* ── Filter Popover ───────────────────────────────────────────── */}
          <Popover open={Boolean(filterAnchor)} anchorEl={filterAnchor} onClose={() => setFilterAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { width: 360, borderRadius: 2.5, border: `1px solid ${C.border2}`, boxShadow: SHADOW_LG, p: 2 } }}>
            <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, mb: 1.5 }}>Time Period</Typography>
            <Box sx={{ display: "flex", gap: 0.75, mb: 2, flexWrap: "wrap" }}>
              {PRESETS.map(p => (
                <Button key={p.days} size="small" onClick={() => { applyPreset(p.days); }}
                  sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", px: 1.5, py: 0.6, minWidth: 0, borderRadius: 20,
                    ...(activePreset === p.days
                      ? { background: C.teal, color: "#fff", "&:hover": { background: "#086460" } }
                      : { border: `1px solid ${C.border2}`, color: C.muted, "&:hover": { borderColor: C.teal, color: C.teal, background: C.tealLt } }) }}>
                  {p.label}
                </Button>
              ))}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, mb: 1.5 }}>Custom Date Range</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <DatePicker label="From" value={startDate} onChange={v => v && setStartDate(v)} maxDate={endDate}
                  slotProps={{ textField: { size: "small", sx: { flex: 1, "& .MuiInputBase-root": { fontFamily: MONO, fontSize: 12 } } } }} />
                <DatePicker label="To" value={endDate} onChange={v => v && setEndDate(v)} minDate={startDate} maxDate={new Date()}
                  slotProps={{ textField: { size: "small", sx: { flex: 1, "& .MuiInputBase-root": { fontFamily: MONO, fontSize: 12 } } } }} />
              </Box>
            </LocalizationProvider>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, mb: 1.5 }}>Region</Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ fontFamily: MONO, fontSize: 11 }}>Country</InputLabel>
                <Select value={country} label="Country" onChange={e => setCountry(e.target.value)} sx={{ fontFamily: MONO, fontSize: 11 }}>
                  <MenuItem value="" sx={{ fontFamily: MONO, fontSize: 11 }}>🌍 All Countries</MenuItem>
                  <MenuItem value="NG" sx={{ fontFamily: MONO, fontSize: 11 }}>🇳🇬 Nigeria (+234)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ fontFamily: MONO, fontSize: 11 }}>State</InputLabel>
                <Select value={state} label="State" onChange={e => setState(e.target.value)} sx={{ fontFamily: MONO, fontSize: 11 }}>
                  <MenuItem value="" sx={{ fontFamily: MONO, fontSize: 11 }}>All States</MenuItem>
                  {(data?.top_states || []).map((s: any) => (
                    <MenuItem key={s.state} value={s.state} sx={{ fontFamily: MONO, fontSize: 11 }}>{s.state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button size="small" onClick={() => setFilterAnchor(null)} sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: C.muted, border: `1px solid ${C.border2}`, borderRadius: 1.5, px: 1.5 }}>Cancel</Button>
              <Button size="small" onClick={() => { fetchData(); setFilterAnchor(null); }} sx={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase", background: C.teal, color: "#fff", borderRadius: 1.5, px: 1.75, "&:hover": { background: "#086460" } }}>Apply</Button>
            </Box>
          </Popover>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* ── AI Insights (animated panel — slides down from header) ────── */}
          <Collapse in={aiOpen} timeout={400}>
            <Box sx={{ mb: 2, borderRadius: 3, overflow: "hidden", boxShadow: SHADOW_LG }}>
              <Box sx={{
                background: `linear-gradient(135deg, ${C.navy} 0%, #0d2a45 50%, ${C.teal}22 100%)`,
                p: "20px 24px 16px",
                borderBottom: `1px solid rgba(255,255,255,.08)`,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
                  <Box sx={{ background: C.teal, color: "#fff", fontFamily: MONO, fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", px: 1, py: 0.4, borderRadius: 10, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AutoAwesome sx={{ fontSize: 10 }} /> AI · Groq
                  </Box>
                  <Typography sx={{ fontFamily: SERIF, fontSize: 16, color: "#fff", flex: 1, letterSpacing: -0.2 }}>Narrative Intelligence Summary</Typography>
                  <IconButton size="small" onClick={() => setAiOpen(false)} sx={{ color: "rgba(255,255,255,.5)", "&:hover": { color: "#fff" } }}>
                    <ExpandLess sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ background: `linear-gradient(180deg, ${C.navy}f5 0%, #0d2a45 100%)` }}>
                <AIInsights startDate={startDate} endDate={endDate} country={country} darkMode />
              </Box>
            </Box>
          </Collapse>

          {/* ── 01 KPI Grid ──────────────────────────────────────────────── */}
          <SectionDivider num="01" label="Key Performance Indicators" />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5, mb: 2 }}>
            <KpiCell label="Users Registered"   value={(reg.total || 0).toLocaleString()}            note={`+${(todayTrend?.new_registrations || 0).toLocaleString()} today`}            noteDir="up"      accent={C.teal}   sparkData={kpiSparklines.registrations} />
            <KpiCell label="Total Messages"     value={(msgs.total || 0).toLocaleString()}           note={`${(todayTrend?.messages || 0).toLocaleString()} today`}                      noteDir="up"      accent={C.green}  sparkData={kpiSparklines.messages} />
            <KpiCell label="Conversations"      value={(conv.period || 0).toLocaleString()}          note={`${(conv.today || 0).toLocaleString()} today`}                                noteDir="up"      accent={C.navy}   sparkData={kpiSparklines.conversations} />
            <KpiCell label="Feedback Positive"  value={fb.positive_rate || 0} unit="%"              note={`${(todayTrend?.feedback_positive || 0).toLocaleString()} positive today`}    noteDir="up"      accent={C.purple} sparkData={kpiSparklines.fbPositive} />
            <KpiCell label="Avg Conv Depth"     value={depth.avg ? (+depth.avg).toFixed(1) : "—"}   note={`${(conv.distinct_contacts || 0).toLocaleString()} active users · turns/session`} noteDir="neutral" accent={C.teal}   sparkData={kpiSparklines.conversations} />
            <KpiCell label="Avg Response Time"  value={((msgs.avg_response_time_ms || 0) / 1000).toFixed(1)} unit="s" note="bot response latency"                                      noteDir="neutral" accent={C.amber}  sparkData={kpiSparklines.messages} />
            <KpiCell label="Service Complaints" value={(cmpl.period || 0).toLocaleString()}          note={`${(todayTrend?.feedback_negative || 0).toLocaleString()} negative feedback today`} noteDir={cmpl.period > 0 ? "down" : "neutral"} accent={C.red} sparkData={kpiSparklines.fbPositive} />
            <KpiCell label="Symptom Checks"     value={(checks.total || 0).toLocaleString()}         note={`${(todayTrend?.conversations || 0).toLocaleString()} conversations today`}   noteDir="up"      accent={C.blue}   sparkData={kpiSparklines.conversations} />
          </Box>

          {/* ── 02 Dual-axis Cumulative Trend ────────────────────────────── */}
          <SectionDivider num="02" label="Cumulative Growth Trend" />
          <Box ref={trendRef}>
          <Card title="Cumulative Conversations vs Daily Registrations"
            sub={`${format(startDate, "MMM d")} – ${format(endDate, "MMM d, yyyy")} · Left Y — conversations · Right Y — daily registrations`}
            sx={{ mb: 2 }}
            action={
              <ChartSettingsButton csvData={trendCsvData} csvFilename={`trend_${format(startDate,"yyyyMMdd")}_${format(endDate,"yyyyMMdd")}.xlsx`}
                chartTypes={["Line", "Area", "Bar"]} activeType={dualAxisType} onTypeChange={setDualAxisType}
                chartRef={trendRef} pngFilename="cumulative_trend.png" storageKey="chart:trend"
                fields={[
                  { axisLabel: "📅 X — Date Dimension",       chips: [{ label: "📅 date (daily)", color: C.muted }], options: ["date (daily)","date (weekly)","date (monthly)"] },
                  { axisLabel: "📈 Y1 — Left (Conversations)", chips: [{ label: "cumulative_conversations", color: C.teal }], options: ["cumulative_conversations","daily_conversations","escalated_conversations"] },
                  { axisLabel: "📊 Y2 — Right (Registrations)",chips: [{ label: "new_registrations", color: C.navy }], options: ["new_registrations","cumulative_users","active_users"] },
                ]}
              />
            }>
            {trends.length === 0 ? (
              <Box sx={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", background: C.panel, borderRadius: 1 }}>
                <Typography sx={{ fontSize: 12, color: C.muted }}>No data in selected period</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={trends} margin={{ top: 4, right: 40, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="gradCumConv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.teal} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={C.teal} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontFamily: MONO, fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="left"  orientation="left"  tick={{ fontFamily: MONO, fontSize: 9, fill: C.teal }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontFamily: MONO, fontSize: 9, fill: C.navy }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ fontFamily: MONO, fontSize: 11, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: SHADOW }} />
                  {(dualAxisType === "Area" || dualAxisType === "Line") ? (
                    <Area yAxisId="left" type="monotone" dataKey="cumulative_conversations" stroke={C.teal} fill={dualAxisType === "Area" ? "url(#gradCumConv)" : "transparent"} strokeWidth={2} name="Cumulative Conversations" dot={false} />
                  ) : (
                    <Bar yAxisId="left" dataKey="cumulative_conversations" fill={C.teal + "99"} name="Cumulative Conversations" radius={[2, 2, 0, 0]} />
                  )}
                  <Line yAxisId="right" type="monotone" dataKey="new_registrations" stroke={C.navy} strokeWidth={2} strokeDasharray="5 4" name="New Registrations" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
            <Box sx={{ display: "flex", gap: 2.5, mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 20, height: 2, background: C.teal }} /><Typography sx={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Cumulative Conversations (left Y)</Typography></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 20, height: 2, background: C.navy, borderTop: `2px dashed ${C.navy}` }} /><Typography sx={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Daily Registrations (right Y)</Typography></Box>
            </Box>
          </Card>
          </Box>

          {/* Side-by-side daily charts (unified Y, type-switchable) */}
          {(() => {
            const maxY = Math.max(...trends.map((d: any) => Math.max(d.conversations || 0, d.new_registrations || 0)), 10);
            const commonAxis = { tick: { fontFamily: MONO, fontSize: 9, fill: C.muted }, tickLine: false as const, axisLine: false as const };
            const renderDailyChart = (dataKey: string, color: string, type: string) => {
              const tip = { contentStyle: { fontFamily: MONO, fontSize: 11, border: `1px solid ${C.border}`, borderRadius: 8 } };
              if (type === "Line") return (
                <LineChart data={trends} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="date" {...commonAxis} interval="preserveStartEnd" />
                  <YAxis {...commonAxis} domain={[0, maxY]} />
                  <Tooltip {...tip} />
                  <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
                </LineChart>
              );
              if (type === "Area") return (
                <AreaChart data={trends} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <defs><linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.2}/><stop offset="95%" stopColor={color} stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="date" {...commonAxis} interval="preserveStartEnd" />
                  <YAxis {...commonAxis} domain={[0, maxY]} />
                  <Tooltip {...tip} />
                  <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#grad-${dataKey})`} strokeWidth={2} dot={false} />
                </AreaChart>
              );
              // Default: Bar
              return (
                <BarChart data={trends} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="date" {...commonAxis} interval="preserveStartEnd" />
                  <YAxis {...commonAxis} domain={[0, maxY]} />
                  <Tooltip {...tip} />
                  <Bar dataKey={dataKey} fill={color + "99"} radius={[2, 2, 0, 0]} />
                </BarChart>
              );
            };
            return (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                <Box ref={userGrowthRef}>
                <Card title="Daily User Growth" sub="New registrations per day"
                  action={<ChartSettingsButton
                    csvData={trends.map((d: any) => ({ date: d.date, new_registrations: d.new_registrations || 0 }))} csvFilename="user_growth.xlsx"
                    chartTypes={["Bar", "Line", "Area"]} activeType={userGrowthType} onTypeChange={setUserGrowthType}
                    chartRef={userGrowthRef} pngFilename="user_growth.png" storageKey="chart:userGrowth"
                    fields={[
                      { axisLabel: "📅 X — Date", chips: [{ label: "📅 date (daily)", color: C.muted }], options: ["date (daily)","date (weekly)","date (monthly)"] },
                      { axisLabel: "📈 Y — Metric", chips: [{ label: "new_registrations", color: C.teal }], options: ["new_registrations","cumulative_users","active_users","returning_users"] },
                      { axisLabel: "🗂 Group By", chips: [{ label: "none", color: C.muted }], options: ["state","language","channel"] },
                    ]} />}>
                  <ResponsiveContainer width="100%" height={155}>
                    {renderDailyChart("new_registrations", C.teal, userGrowthType)}
                  </ResponsiveContainer>
                </Card>
                </Box>

                <Box ref={convVolumeRef}>
                <Card title="Daily Conversation Volume" sub="Conversations per day · unified Y scale"
                  action={<ChartSettingsButton
                    csvData={trends.map((d: any) => ({ date: d.date, conversations: d.conversations }))} csvFilename="conv_volume.xlsx"
                    chartTypes={["Bar", "Line", "Area"]} activeType={convVolumeType} onTypeChange={setConvVolumeType}
                    chartRef={convVolumeRef} pngFilename="conv_volume.png" storageKey="chart:convVolume"
                    fields={[
                      { axisLabel: "📅 X — Date", chips: [{ label: "📅 date (daily)", color: C.muted }], options: ["date (daily)","date (weekly)","date (monthly)"] },
                      { axisLabel: "📊 Y — Metric", chips: [{ label: "daily_conversations", color: C.navy }], options: ["daily_conversations","messages_sent","messages_incoming","avg_response_time_ms","complaints"] },
                      { axisLabel: "🗂 Group By", chips: [{ label: "none", color: C.muted }], options: ["state","channel","language"] },
                    ]} />}>
                  <ResponsiveContainer width="100%" height={155}>
                    {renderDailyChart("conversations", C.navy, convVolumeType)}
                  </ResponsiveContainer>
                </Card>
                </Box>
              </Box>
            );
          })()}

          {/* ── 03 User Reach & Journey ──────────────────────────────────── */}
          <SectionDivider num="03" label="User Reach & Journey" />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2, alignItems: "stretch" }}>
            {/* Language */}
            <DonutCard title="Language Distribution" sub="Conversations by language"
              data={[
                { name: "English", value: (data?.language_distribution || []).find((r: any) => r.lang === "en")?.count || 0 },
                { name: "Hausa",   value: (data?.language_distribution || []).find((r: any) => r.lang === "ha")?.count || 0 },
                { name: "Yoruba",  value: (data?.language_distribution || []).find((r: any) => r.lang === "yo")?.count || 0 },
                { name: "Igbo",    value: (data?.language_distribution || []).find((r: any) => r.lang === "ig")?.count || 0 },
              ].filter(d => d.value > 0)}
              colors={[C.teal, C.purple, C.amber, C.blue]}
              label={(data?.language_distribution || []).reduce((s: number, r: any) => s + r.count, 0).toLocaleString()} sublabel="total"
              csvData={(data?.language_distribution || []).map((r: any) => ({ language: r.lang, count: r.count }))} csvFilename="language_distribution.xlsx"
              fields={[
                { axisLabel: "🔵 Slice by",  chips: [{ label: "language",          color: C.teal }], options: ["language","state","channel","age_group"] },
                { axisLabel: "📊 Value",     chips: [{ label: "conversation_count", color: C.navy }], options: ["conversation_count","user_count","percentage"] },
                { axisLabel: "📅 Date Range",chips: [{ label: "date (last 30d)",    color: C.muted }], options: ["date (today)","date (last 7d)","date (last 30d)","date (last 90d)"] },
              ]}
            />
            {/* User Journey Funnel */}
            <Box ref={funnelRef} sx={{ height: "100%" }}>
            <Card title="User Journey Funnel" sub="Unique users at each stage" sx={{ height: "100%" }}
              action={<ChartSettingsButton csvData={FUNNEL_STAGES.map(s => ({ stage: s.label, users: (data?.funnel || {})[s.key] || 0 }))} csvFilename="user_funnel.xlsx"
                chartTypes={["Funnel", "H. Bar", "Waterfall"]} activeType={funnelType} onTypeChange={setFunnelType}
                chartRef={funnelRef} pngFilename="user_funnel.png" storageKey="chart:funnel"
                fields={[
                  { axisLabel: "🔵 Stage", chips: [{ label: "journey_stage (fixed)", color: C.teal }], options: [] },
                  { axisLabel: "📊 Value", chips: [{ label: "user_count",             color: C.navy }], options: ["user_count","percentage","drop_off_rate"] },
                  { axisLabel: "🗂 Group By", chips: [{ label: "none", color: C.muted }], options: ["state","language","channel"] },
                ]} />}>
              <UserFunnel funnel={data?.funnel} chartType={funnelType} />
            </Card>
            </Box>
          </Box>

          {/* ── 04 Geographic Distribution ───────────────────────────────── */}
          {(data?.top_states || []).length > 0 && (
            <>
            <SectionDivider num="04" label="Geographic Distribution" />
            <Box ref={topStatesRef}>
            <Card title="Top States" sub="Registrations by state" sx={{ mb: 2 }}
              action={<ChartSettingsButton csvData={(data?.top_states || []).map((s: any) => ({ state: s.state, count: s.count }))} csvFilename="top_states.xlsx" chartTypes={["H. Bar", "V. Bar"]} chartRef={topStatesRef} pngFilename="top_states.png" fields={[{ axisLabel: "📊 Y — Value", chips: [{ label: "count", color: C.teal }], options: ["count","percentage"] }]} />}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
                {(data.top_states as any[]).map((s: any, i: number) => {
                  const maxCount = data.top_states[0]?.count || 1;
                  const pct = Math.round((s.count / maxCount) * 100);
                  const colors = [C.teal, C.blue, C.purple, C.green, C.amber, C.navy, C.teal, C.blue, C.purple, C.green];
                  return (
                    <Box key={s.state}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                        <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.ink }}>{s.state}</Typography>
                        <Typography sx={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{s.count.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ height: 5, borderRadius: 3, background: C.border, overflow: "hidden" }}>
                        <Box sx={{ height: "100%", width: `${pct}%`, background: colors[i % colors.length], borderRadius: 3 }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Card>
            </Box>
            </>
          )}

          {/* ── 05 User Feedback ─────────────────────────────────────────── */}
          <SectionDivider num="05" label="User Feedback" />
          <Box sx={{ mb: 2 }}>
            <FeedbackChart
              fb={fb}
              trends={trends}
              showAll={showAllFb}
              onToggleAll={() => setShowAllFb(v => !v)}
              onExport={() => {
                const entries = fb.entries || [];
                if (!entries.length) return;
                const ws = XLSX.utils.json_to_sheet(entries.map((e: any) => ({ Date: e.date, Satisfaction: e.satisfaction, Rating: e.rating ?? "", Source: e.source, Comment: e.comment })));
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Feedback");
                XLSX.writeFile(wb, `feedback_${format(startDate, "yyyyMMdd")}_${format(endDate, "yyyyMMdd")}.xlsx`);
              }}
            />
          </Box>

          {/* ── 06 Disease Diagnostics ───────────────────────────────────── */}
          <SectionDivider num="06" label="Disease Diagnostics" />
          <Box ref={diseaseRef}>
          <Card
            title={`Disease Distribution — ${diseaseType}`}
            sub="Diagnostic assessments by disease · click slice to drill into severity"
            sx={{ mb: 2 }}
            action={<ChartSettingsButton csvData={[{ disease: "Malaria", total: dis.malaria?.total || 0 }, { disease: "HIV", total: dis.hiv?.total || 0 }, { disease: "TB", total: dis.tb?.total || 0 }]} csvFilename="disease_distribution.xlsx"
              chartTypes={["Bar", "Donut", "Pie"]} activeType={diseaseType} onTypeChange={setDiseaseType}
              chartRef={diseaseRef} pngFilename="disease_distribution.png" storageKey="chart:disease"
              fields={[
                { axisLabel: "🔵 Inner Ring — Disease",  chips: [{ label: "disease_type", color: C.blue }],  options: ["disease_type","severity"] },
                { axisLabel: "🔴 Outer Ring — Severity", chips: [{ label: "severity",      color: C.amber }], options: ["severity","disease_type","state"] },
                { axisLabel: "📊 Value",                 chips: [{ label: "assessment_count", color: C.teal }], options: ["assessment_count","percentage","avg_score_pct"] },
              ]} />}>
            <SunburstChart diseases={dis} chartType={diseaseType} />
          </Card>
          </Box>

          {/* ── 07 Conversation Depth Distribution ──────────────────────── */}
          <SectionDivider num="07" label="Conversation Depth Distribution" />
          <Box ref={depthRef}>
          <Card title="Message Depth Buckets" sub="User count by engagement depth · period"
            sx={{ mb: 2 }}
            action={<ChartSettingsButton csvData={(depth.histogram || []).map((r: any) => ({ bucket: r.bucket, user_count: r.count }))} csvFilename="depth_histogram.xlsx"
              chartTypes={["H. Bar", "V. Bar", "Donut", "Pie"]} activeType={depthType} onTypeChange={setDepthType}
              chartRef={depthRef} pngFilename="depth_histogram.png" storageKey="chart:depth"
              fields={[
                { axisLabel: "📅 X — Depth Bucket", chips: [{ label: "depth_bucket", color: C.muted }], options: ["depth_bucket (1-2,3-5,6-8,9-10,>10)","depth_bucket (1-3,3-7,7+)"] },
                { axisLabel: "📊 Y — Count Metric",  chips: [{ label: "user_count",   color: C.blue }],  options: ["user_count","conversation_count","percentage"] },
                { axisLabel: "🗂 Group By",           chips: [{ label: "none",         color: C.muted }], options: ["language","state","channel","date (weekly)"] },
              ]} />}>
            <DepthHistogram histogram={depth.histogram || []} chartType={depthType} />
          </Card>
          </Box>

          {/* ── 08 Health Topics ─────────────────────────────────────────── */}
          <SectionDivider num="08" label="What Users Care About" />
          <Box sx={{ mb: 2 }}>
            <HealthTopicsDrillDown interests={data?.top_interests || []} />
          </Box>

          {/* ── 09 Facility Map ──────────────────────────────────────────── */}
          <SectionDivider num="09" label="Facility Search Locations" />
          <Box ref={facilityMapRef}>
          <Card title="Where Users Are Searching"
            sub={`${mapPoints.length.toLocaleString()} search events · Nigeria LGA choropleth heatmap`}
            action={<ChartSettingsButton csvData={mapPoints.map((p: any) => ({ lat: p.lat, lon: p.lon, count: p.count || 1 }))} csvFilename="facility_searches.xlsx"
              chartTypes={["Choropleth", "Dot Markers"]} chartRef={facilityMapRef} pngFilename="facility_map.png" fields={[
                { axisLabel: "🔥 Intensity", chips: [{ label: "search_count", color: C.teal }], options: ["search_count","user_count","complaint_count"] },
                { axisLabel: "📅 Date Range", chips: [{ label: "date (last 30d)", color: C.muted }], options: ["date (today)","date (last 7d)","date (last 30d)","date (last 90d)"] },
                { axisLabel: "🗂 Region Filter", chips: [{ label: "none", color: C.muted }], options: ["state","LGA","facility_type"] },
              ]} />}>
            {mapPoints.length === 0 ? (
              <Box sx={{ height: 420, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.panel, borderRadius: 1, gap: 1.5 }}>
                <MapIcon sx={{ fontSize: 40, color: C.border2 }} />
                <Typography sx={{ fontSize: 13, color: C.muted }}>No facility searches in this period</Typography>
              </Box>
            ) : (
              <FacilityMapEmbed points={mapPoints} />
            )}
          </Card>
          </Box>

        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
